import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useGlobal } from 'context/GlobalContext';
import { useToast } from 'context/ToastContext';
import { useSettings } from 'context/SettingsContext';
import { exportObjectsToExcel } from './exportObjectsToExcel';
import { defaultObjectStatuses, defaultSaleStatuses, useObject } from '../context/ObjectProvider';
import * as apiObject from 'api/apiObjects';
import { createOffDateFilter } from 'modules/admin';
import { useListRefreshEffect } from 'utils/helpers/list/useListRefreshEffect';
import { useListOnPage } from 'utils/helpers/list/useListOnPage';
import type { GetObjectListParamsType, GetObjectListResultType, ObjectType } from '@econtractor/types';

const buildObjectsOrder = (order: string, desc: boolean) =>
    `${order ? `order by ${order} ` : ''}${desc ? 'DESC' : ''}`;

const normalizePaginatorPage = (value: unknown): number => {
    const raw = Array.isArray(value) ? value[0] : value;
    const page = Number(raw);
    return Number.isFinite(page) && page !== 0 ? page : 1;
};

const useObjectsList = () => {
    const { t } = useTranslation(['Objects', 'common', 'Reports']);
    const { g } = useGlobal();
    const { showToast } = useToast();
    const { localSettings } = useSettings();
    const {
        showSpinner,
        setShowSpinner,
        objectsDesc,
        objectsOrder,
        filterStatus,
        setFilterStatus,
        filterTeamId,
        setFilterTeamId,
        filterClientId,
        setFilterClientId,
        filterOnlyOverdue,
        setFilterOnlyOverdue,
        objectsDateFilter,
        setObjectsDateFilter,
        setObjectsOrder,
        setObjectsDesc,
        showDebtor,
        showClient,
        showTeam,
        showCreated,
        showAccess,
        showInWork,
        listResetNonce,
        listRefreshNonce,
        onlySale,
        onlyService,
    } = useObject();

    const [objectsList, setObjectsList] = useState<ObjectType[] | null>(null);
    const [exporting, setExporting] = useState(false);
    const [paidUsd, setPaidUsd] = useState(0);
    const [paidUah, setPaidUah] = useState(0);
    const [page, setPage] = useState<number | null>(null);
    const [clientInfoId, setClientInfoId] = useState<number | null>(null);
    const [tasksObjectId, setTasksObjectId] = useState<number | null>(null);
    const [listFetchNonce, setListFetchNonce] = useState(0);

    const location = useLocation();
    const onPage = useListOnPage();
    const prevListData = useRef<string | null>(null);
    const forceNextFetchRef = useRef(false);
    const previousDatesRef = useRef(objectsDateFilter.dates);

    const getListParams = (): GetObjectListParamsType => ({
        search: `%${g?.search?.[location.pathname] || ''}%`,
        order: buildObjectsOrder(objectsOrder, objectsDesc),
        page: page < 0 ? 1 : normalizePaginatorPage(page),
        onPage,
        status: filterStatus,
        ...(filterTeamId ? { teamId: filterTeamId } : {}),
        ...(filterClientId ? { clientId: filterClientId } : {}),
        ...(!onlySale && !onlyService && filterOnlyOverdue ? { onlyOverdue: true } : {}),
        ...(!onlySale && !onlyService && objectsDateFilter.dates ? {
            dateFrom: objectsDateFilter.dates[0],
            dateTo: objectsDateFilter.dates[1],
        } : {}),
        onlySale,
        onlyService,
    });

    const exportObjects = async (): Promise<void> => {
        if (exporting || showSpinner || objectsList === null) return;
        const params = getListParams();
        const view = { showClient, showTeam, showCreated, showAccess, showInWork, showDebtor, onlySale, onlyService };
        const rate = Number(localSettings?.RATE?.current ?? 1) || 1;
        setExporting(true);
        try {
            const objects: ObjectType[] = [];
            let exportPage = 1;
            let countPages = 1;
            do {
                const result = (await apiObject.getList({ ...params, page: exportPage, onPage: 100 })) as GetObjectListResultType | null;
                if (!result) throw new Error('Object export failed');
                objects.push(...result.objects);
                countPages = result.paginator.countPages;
                exportPage += 1;
            } while (exportPage <= countPages);
            if (!objects.length) {
                showToast(t('Reports:export.empty'), 'warning');
                return;
            }
            await exportObjectsToExcel(objects, view, rate, t);
        } catch {
            showToast(t('common:messError'), 'danger');
        } finally {
            setExporting(false);
        }
    };

    const fetchObjects = async (force = false, options?: { silent?: boolean }): Promise<void | null> => {
        if (force) prevListData.current = null;
        if (!page || showSpinner) return null;
        const listData = getListParams();

        // Запобіжник для подвійного запиту
        if (prevListData.current === JSON.stringify(listData)) return null;
        prevListData.current = JSON.stringify(listData);

        const silent = Boolean(options?.silent);
        if (!silent) setShowSpinner(true);
        const resObjects = (await apiObject.getList(listData)) as GetObjectListResultType | null;
        if (!resObjects) {
            if (!silent) setShowSpinner(false);
            showToast(t('common:messError'), 'danger');
            return null;
        }
        if (!silent) setShowSpinner(false);

        g.setPaginator((prev: Record<string, unknown>) => ({
            ...prev,
            [location.pathname]: resObjects.paginator,
        }));

        setObjectsList(resObjects.objects);
        setPaidUsd(Number(resObjects.paidUsd || 0));
        setPaidUah(Number(resObjects.paidUah || 0));
        return null;
    };

    const bumpListFetch = useCallback(() => {
        prevListData.current = null;
        forceNextFetchRef.current = true;
        setListFetchNonce((nonce) => nonce + 1);
    }, []);

    const resetObjectsList = useCallback(() => {
        setObjectsOrder('');
        setObjectsDesc(false);
        setFilterStatus(onlySale || onlyService ? [...defaultSaleStatuses] : [...defaultObjectStatuses]);
        setFilterTeamId(null);
        setFilterClientId(null);
        setFilterOnlyOverdue(false);
        setObjectsDateFilter(createOffDateFilter());
        g.setPaginatorPage(location.pathname, 1);
        setPage(1);
        bumpListFetch();
    }, [
        bumpListFetch,
        g,
        location.pathname,
        setFilterClientId,
        setFilterOnlyOverdue,
        setObjectsDateFilter,
        setFilterStatus,
        setFilterTeamId,
        setObjectsDesc,
        setObjectsOrder,
        onlySale,
        onlyService,
    ]);

    useListRefreshEffect(listResetNonce, () => {
        resetObjectsList();
    });

    useListRefreshEffect(listRefreshNonce, () => {
        bumpListFetch();
    });

    // Хук на пошук
    useEffect(() => {
        if (page === 1) {
            void fetchObjects(true);
        } else {
            setPage(1);
        }
    }, [g.search?.[location.pathname], g.target]);

    // Хук на зміну сторінки / фільтрів / refresh|reset
    useEffect(() => {
        if (previousDatesRef.current !== objectsDateFilter.dates) {
            previousDatesRef.current = objectsDateFilter.dates;
            g.setPaginatorPage(location.pathname, 1);
            if (page !== 1) {
                setPage(1);
                return;
            }
        }
        const force = forceNextFetchRef.current;
        forceNextFetchRef.current = false;
        void fetchObjects(force);
    }, [page, filterStatus, filterTeamId, filterClientId, filterOnlyOverdue, objectsDateFilter.dates, objectsOrder, objectsDesc, onPage, listFetchNonce]);

    // Змінюємо "внутрішню сторінку" при зміні пагінатора
    useEffect(() => {
        const _page = normalizePaginatorPage(g?.paginator?.[location.pathname]?.currentPage);
        if (page !== _page) setPage(_page);
    }, [
        g?.paginator?.[location.pathname]?.currentPage,
        g?.paginator?.[location.pathname]?.countRows,
        g?.paginator?.[location.pathname]?.onPage,
    ]);

    const tasksContextName =
        objectsList?.find((item) => item.ID === tasksObjectId)?.objectName
        || objectsList?.find((item) => item.ID === tasksObjectId)?.clientName
        || null;

    return {
        objectsList,
        paidUsd,
        paidUah,
        showDebtor,
        clientInfoId,
        setClientInfoId,
        tasksObjectId,
        setTasksObjectId,
        tasksContextName,
        fetchObjects,
        exportObjects,
        exporting,
        exportDisabled: exporting || showSpinner || objectsList === null,
    };
};

export default useObjectsList;
